import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastOptions {
  href?: string;
  durationMs?: number;
  onClick?: () => void;
}

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
  href?: string;
  onClick?: () => void;
}

/** How long a toast stays on screen before it auto-dismisses. */
const TOAST_DURATION_MS = 2400;

interface UiStore {
  toasts: Toast[];
  notify: (message: string, tone?: ToastTone, options?: ToastOptions) => void;
  dismiss: (id: string) => void;
}

export const useUiStore = create<UiStore>((set, get) => ({
  toasts: [],

  notify: (message, tone = 'success', options) => {
    const id = crypto.randomUUID();
    set((s) => ({
      toasts: [
        ...s.toasts,
        {
          id,
          message,
          tone,
          ...(options?.href ? { href: options.href } : {}),
          ...(options?.onClick ? { onClick: options.onClick } : {}),
        },
      ],
    }));

    // Self-expiring so no component has to own dismissal.
    setTimeout(() => get().dismiss(id), options?.durationMs ?? TOAST_DURATION_MS);
  },

  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative access for non-React callers. */
export const notify = (
  message: string,
  tone: ToastTone = 'success',
  options?: ToastOptions,
) => useUiStore.getState().notify(message, tone, options);
